import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api, type EmailDelivery } from '../api/client';
import { BarraDeFiltros, Buscador, LimpiarFiltros, Segmentado } from '../components/Filtros';
import { AvisoError } from '../components/Aviso';

/**
 * Qué correos intentó mandar el portal y cómo terminó cada uno.
 *
 * Existe porque la pregunta «¿le llegó la invitación?» sólo se podía contestar
 * leyendo el log de un contenedor. Y cuando el envío falló durante días, el
 * portal seguía mostrando la cuenta como invitada — lo contrario de lo que había
 * pasado.
 *
 * Los fallidos van arriba de su propia cuenta y con el motivo completo: es la
 * única información que sirve cuando alguien reporta que no le llegó nada.
 */
/** Compara sin acentos ni mayúsculas: quien escribe «credito» busca «Crédito». */
const coincide = (texto: string, busca: string) =>
  texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .includes(
      busca
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase(),
    );

export function Correos() {
  const { t } = useTranslation();
  const [envios, setEnvios] = useState<EmailDelivery[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [resultado, setResultado] = useState('');

  const cargar = useCallback(async () => {
    try {
      setEnvios(await api.emailDeliveries());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const todos = envios ?? [];
  const fallidos = todos.filter((e) => e.outcome === 'FAILED').length;
  const visibles = todos.filter(
    (e) =>
      (!resultado || e.outcome === resultado) &&
      (!busca || coincide(`${e.username} ${e.email}`, busca)),
  );

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('users.emailsTitle')}</h1>
        <p className="pagina__bajada">{t('users.emailsExplanation')}</p>
      </div>

      <AvisoError error={error} />

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('users.emailsTitle')}</h2>
          {fallidos > 0 && (
            <span className="pildora pildora--critico">
              <span className="pildora__punto" aria-hidden="true" />
              {fallidos}
            </span>
          )}
        </div>

        <BarraDeFiltros>
          <Buscador id="buscarCorreo" etiqueta={t('users.search')} valor={busca} onCambio={setBusca} />
          <Segmentado
            etiqueta={t('users.emailOutcome')}
            valor={resultado}
            onCambio={setResultado}
            opciones={[
              { valor: '', etiqueta: t('users.allStatuses'), conteo: todos.length },
              { valor: 'SENT', etiqueta: t('users.emailSent'), conteo: todos.length - fallidos },
              { valor: 'FAILED', etiqueta: t('users.emailFailed'), conteo: fallidos },
            ]}
          />
          <div className="barra-filtros__acciones">
            <LimpiarFiltros
              hay={Boolean(busca || resultado)}
              onLimpiar={() => {
                setBusca('');
                setResultado('');
              }}
            />
          </div>
        </BarraDeFiltros>

        {envios && visibles.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">
              {busca || resultado ? t('common.noResults') : t('users.emailsEmpty')}
            </span>
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('users.emailWhen')}</th>
                  <th>{t('users.person')}</th>
                  <th>{t('users.email')}</th>
                  <th>{t('users.emailKind')}</th>
                  <th>{t('users.emailOutcome')}</th>
                  <th>{t('users.emailRequestedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((envio) => (
                  <tr key={envio.id}>
                    <td>{new Date(envio.attemptedAt).toLocaleString(i18n.language)}</td>
                    <td className="mono">{envio.username}</td>
                    <td className="mono">{envio.email}</td>
                    <td>
                      {t(`users.emailKind_${envio.kind}`)}
                    </td>
                    <td>
                      <span
                        className={`pildora pildora--${envio.outcome === 'SENT' ? 'bueno' : 'critico'}`}
                      >
                        <span className="pildora__punto" aria-hidden="true" />
                        {t(envio.outcome === 'SENT' ? 'users.emailSent' : 'users.emailFailed')}
                      </span>
                      {/* El motivo, completo y sin recortar. Es lo único que
                          sirve cuando alguien reporta que no le llegó nada, y
                          recortarlo obligaría a ir al log igual. */}
                      {envio.detail && <div className="campo__ayuda">{envio.detail}</div>}
                    </td>
                    <td className="mono">{envio.requestedByUsername ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
